'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import ArticleNavigationNode from '@organisms/ArticleNavigationMenu/ArticleNavigationNode';
import { StyledNavigationMenu } from './ArticleNavigationMenu.styles';
import useResponsive from '@hooks/useResponsive';
import useNavigationData from './useNavigationData';

interface Props {
  articleUid: string;
}

const ArticleNavigationMenu = ({ articleUid }: Props) => {
  const pathname = usePathname();
  const [activeNode, setActiveNode] = useState<ActiveNodeType>({ id: '', parents: [] });
  const [expandedNodes, setExpandedNodes] = useState<Array<NavigationNode>>([]);
  const [expandedNavigationNodes, setExpandedNavigationNodes] = useState<Array<NavigationNode>>([]);
  const { data: navigationData } = useNavigationData(articleUid);
  const { fromDesktop } = useResponsive();

  useEffect(() => {
    if (navigationData && navigationData?.children?.length > 0) {
      let continueExec = true;

      const getActiveNavigationNode = (node: ArticleCMSNode, parentNodes: NavigationNode[]) => {
        if (continueExec) {
          const currentHierarchy = parentNodes.concat({ id: node.title });

          const activeLink = node.links?.find((link: CMSLink) => {
            // pathname already excludes query string in App Router
            return link.url === pathname;
          });

          if (activeLink) {
            continueExec = false;
            setActiveNode({ id: activeLink.title, parents: parentNodes });
            setExpandedNodes(currentHierarchy);
          } else {
            node?.children?.forEach((child) => getActiveNavigationNode(child, currentHierarchy));
          }
        }
      };

      getActiveNavigationNode(navigationData, [] as NavigationNode[]);
    }
  }, [navigationData, pathname]);

  return fromDesktop && navigationData ? (
    <StyledNavigationMenu data-testid="article-navigation-menu">
      {navigationData?.children?.length > 0 &&
        navigationData.children.map((child: ArticleCMSNode, index) => (
          <ArticleNavigationNode
            node={child}
            activeNode={activeNode}
            setActiveNode={setActiveNode}
            navigationNodes={expandedNavigationNodes}
            setNavigationNodes={setExpandedNavigationNodes}
            parentNodes={[]}
            setExpandedNodes={setExpandedNodes}
            expandedNodes={expandedNodes}
            key={`${child.title}-${index}`}
          />
        ))}
    </StyledNavigationMenu>
  ) : null;
};

export default ArticleNavigationMenu;
